import React, { useEffect, useContext } from "react";
import { useLocation, Link, useHistory } from "react-router-dom";
import { Alert, Button, Input, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import ValetApi from "../api/Api";
import GarageCard from "./GarageCard";
import UserContext from "../auth/UserContext";
import "../transactions/GarageList.css";
import useGarageSearchHandlers from "../utility/useGarageSearchHandlers";
import { parseGarageMobileSearch } from "../utility/garageMobileSearch";
import MobileSearchBar from "../components/MobileSearchBar";
import { toast } from "react-toastify";
import { getDateInputValueInTimeZone } from "../utility/dateInput";
import { createGarageCacheScope, readGarageSnapshot, saveGarageSnapshot, removeGarageSnapshot, clearGarageSnapshots } from "../utility/garageOfflineCache";

const GARAGE_FILTERS_STORAGE_KEY = "garageList.filters.v1";
const VALID_VIEW_MODES = ["operational", "checked_out", "all"];
const VALID_SORT_BY = ["default", "mobile", "ticket", "make", "status", "arrival", "departure"];

function readStoredGarageFilters() {
    try {
        const raw = window.localStorage.getItem(GARAGE_FILTERS_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return {
            showFilters: Boolean(parsed?.showFilters),
            viewMode: VALID_VIEW_MODES.includes(parsed?.viewMode) ? parsed.viewMode : "operational",
            sortBy: VALID_SORT_BY.includes(parsed?.sortBy) ? parsed.sortBy : "default",
        };
    } catch {
        return null;
    }
}

/** Show Transactions for active user location **/
const GarageList = () => {
    const location = useLocation();
    const history = useHistory();

    const { mobile: mobileQuery, podiumId: podiumIdQuery, lostkeys: lostKeysQuery } = parseGarageMobileSearch(location.search);

    const { userDetail, activePodiumId, activeLocationId, setError, garageData, setGarageData } = useContext(UserContext);

    const hasAccessiblePodiums = userDetail?.accessiblePodiums && userDetail.accessiblePodiums.length > 0;
    const userTimeZone = userDetail?.userTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    const todayInUserTimeZone = getDateInputValueInTimeZone(new Date(), userTimeZone);
    const persistedFilters = React.useMemo(() => readStoredGarageFilters(), []);
    const [viewMode, setViewMode] = React.useState(persistedFilters?.viewMode || "operational");
    const [sortBy, setSortBy] = React.useState(persistedFilters?.sortBy || "default");
    const [showFilters, setShowFilters] = React.useState(Boolean(persistedFilters?.showFilters));
    const [paymentByVehicleId, setPaymentByVehicleId] = React.useState({});
    const [isPaymentsFeatureEnabled, setIsPaymentsFeatureEnabled] = React.useState(false);
    const [showTransferAllModal, setShowTransferAllModal] = React.useState(false);
    const [destinationPodiums, setDestinationPodiums] = React.useState([]);
    const [transferDestinationPodiumId, setTransferDestinationPodiumId] = React.useState("");
    const [transferNote, setTransferNote] = React.useState("");
    const [isTransferAllBusy, setIsTransferAllBusy] = React.useState(false);
    const [transferAllPreviewCount, setTransferAllPreviewCount] = React.useState(0);
    const [isTransferAllPreviewLoading, setIsTransferAllPreviewLoading] = React.useState(false);
    const [refreshGarageTick, setRefreshGarageTick] = React.useState(0);
    const [offlineState, setOfflineState] = React.useState(null);
    const [cacheWarning, setCacheWarning] = React.useState(false);

    const fallbackPodiumId = hasAccessiblePodiums ? Number(userDetail.accessiblePodiums[0].id) : Number(userDetail?.podiumId) || null;

    // query podium takes priority, then active podium, then fallback
    const selectedPodiumId = Number(podiumIdQuery || activePodiumId || fallbackPodiumId) || null;

    const selectedPodium = userDetail?.accessiblePodiums?.find((p) => Number(p.id) === Number(selectedPodiumId)) || null;

    const activePodiumName = selectedPodium?.name || "No podium";

    useGarageSearchHandlers(userDetail, setGarageData, setError);

    useEffect(() => {
        let cancelled = false;

        async function fetchPaymentsFeatureStatus() {
            try {
                const res = await ValetApi.getPaymentsFeatureStatus();
                if (!cancelled) {
                    const isEnabled = Boolean(res?.data?.featureEnabled === true);
                    setIsPaymentsFeatureEnabled(isEnabled);
                }
            } catch {
                if (!cancelled) {
                    setIsPaymentsFeatureEnabled(false);
                    setPaymentByVehicleId({});
                }
            }
        }

        fetchPaymentsFeatureStatus();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (lostKeysQuery !== "true") setGarageData([]);
        setOfflineState(null);
        setCacheWarning(false);
    }, [selectedPodiumId, userDetail?.id, lostKeysQuery, setGarageData]);

    useEffect(() => {
        const refresh = () => setRefreshGarageTick((previous) => previous + 1);
        const interval = window.setInterval(refresh, 30000);
        window.addEventListener("online", refresh);
        return () => {
            window.clearInterval(interval);
            window.removeEventListener("online", refresh);
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        async function fetchTransactions() {
            if (lostKeysQuery === "true" || !selectedPodiumId) return;
            const scope = await createGarageCacheScope(userDetail, selectedPodiumId);
            const filterMobile = (rows) => {
                const digits = String(mobileQuery || "").replace(/\D/g, "");
                return mobileQuery
                    ? rows.filter(
                          (row) =>
                              digits &&
                              String(row?.vehicleMobile || "")
                                  .replace(/\D/g, "")
                                  .includes(digits),
                      )
                    : rows;
            };
            try {
                const res = await ValetApi.getGarageCardsByPodiumId(selectedPodiumId, userTimeZone, todayInUserTimeZone, viewMode, sortBy);
                if (cancelled || (scope && !scope.isCurrent())) return;
                if (!Array.isArray(res?.data?.garageData)) throw new Error("Invalid garage response");
                const rows = res.data.garageData;
                setGarageData(filterMobile(rows));
                setOfflineState(null);
                if (viewMode !== "checked_out") {
                    const saved = await saveGarageSnapshot(scope, rows);
                    if (!cancelled) setCacheWarning(!saved);
                }
            } catch (err) {
                if (cancelled) return;
                if (err?.isUnavailable) {
                    const snapshot = await readGarageSnapshot(scope);
                    if (cancelled) return;
                    setGarageData(filterMobile(snapshot?.vehicles || []));
                    setOfflineState({ savedAt: snapshot?.savedAt || null });
                    setShowTransferAllModal(false);
                    return;
                }
                if (err?.status === 401) await clearGarageSnapshots();
                if (err?.status === 403) await removeGarageSnapshot(scope);
                if (cancelled) return;
                setGarageData([]);
                setOfflineState(null);
                toast.error(err?.[0] || err?.message || "Unable to load garage", { autoClose: 1000 });
            }
        }

        fetchTransactions();
        return () => {
            cancelled = true;
        };
    }, [selectedPodiumId, userDetail, lostKeysQuery, mobileQuery, setGarageData, userTimeZone, todayInUserTimeZone, viewMode, sortBy, refreshGarageTick]);

    useEffect(() => {
        let cancelled = false;
        async function fetchPaymentBadges() {
            if (offlineState || isPaymentsFeatureEnabled !== true) {
                setPaymentByVehicleId({});
                return;
            }

            if (!Array.isArray(garageData) || garageData.length === 0 || !selectedPodiumId) {
                setPaymentByVehicleId({});
                return;
            }

            const vehicleIds = [...new Set(garageData.map((row) => row.vehicleId))];
            setPaymentByVehicleId({});
            const retryIds = [];

            async function loadStatus(vehicleId, allowRetry) {
                try {
                    const res = await ValetApi.getVehiclePaymentStatus(vehicleId);
                    if (!res?.data?.status) throw new Error("Invalid payment status response");
                    if (!cancelled) setPaymentByVehicleId((current) => ({ ...current, [vehicleId]: res.data.status }));
                } catch (err) {
                    if (cancelled) return;
                    const canRetry = !err?.status || err.status >= 500;
                    if (allowRetry && canRetry) {
                        retryIds.push(vehicleId);
                    } else {
                        setPaymentByVehicleId((current) => ({ ...current, [vehicleId]: { loadState: "error" } }));
                    }
                }
            }

            async function loadInBatches(ids, allowRetry) {
                for (let offset = 0; offset < ids.length && !cancelled; offset += 4) {
                    await Promise.all(ids.slice(offset, offset + 4).map((vehicleId) => loadStatus(vehicleId, allowRetry)));
                }
            }

            await loadInBatches(vehicleIds, true);
            await loadInBatches(retryIds, false);
        }

        fetchPaymentBadges();
        return () => {
            cancelled = true;
        };
    }, [garageData, selectedPodiumId, isPaymentsFeatureEnabled, offlineState]);

    useEffect(() => {
        async function fetchDestinationPodiums() {
            if (!showTransferAllModal) return;

            const locationId = Number(selectedPodium?.locationId || activeLocationId || userDetail?.locationId);
            if (!locationId || !selectedPodiumId) {
                setDestinationPodiums([]);
                return;
            }

            try {
                const res = await ValetApi.getPodiumsByLocation(locationId);
                const allPodiums = Array.isArray(res?.data?.podiums) ? res.data.podiums : [];
                const options = allPodiums.filter((p) => Number(p.id) !== Number(selectedPodiumId));
                setDestinationPodiums(options);
            } catch (err) {
                setDestinationPodiums([]);
                toast.error(err?.[0] || err?.response?.data?.error?.message || "Error loading destination podiums", { autoClose: 1200 });
            }
        }

        fetchDestinationPodiums();
    }, [showTransferAllModal, selectedPodium?.locationId, activeLocationId, userDetail?.locationId, selectedPodiumId]);

    useEffect(() => {
        let cancelled = false;

        async function fetchTransferAllPreviewCount() {
            if (!showTransferAllModal || !selectedPodiumId) {
                setTransferAllPreviewCount(0);
                setIsTransferAllPreviewLoading(false);
                return;
            }

            try {
                setIsTransferAllPreviewLoading(true);
                const res = await ValetApi.getGarageCardsByPodiumId(selectedPodiumId, userTimeZone, todayInUserTimeZone, "operational", "default");
                if (cancelled) return;
                const rows = Array.isArray(res?.data?.garageData) ? res.data.garageData : [];
                setTransferAllPreviewCount(rows.length);
            } catch {
                if (!cancelled) {
                    setTransferAllPreviewCount(0);
                }
            } finally {
                if (!cancelled) {
                    setIsTransferAllPreviewLoading(false);
                }
            }
        }

        fetchTransferAllPreviewCount();

        return () => {
            cancelled = true;
        };
    }, [showTransferAllModal, selectedPodiumId, userTimeZone, todayInUserTimeZone]);

    useEffect(() => {
        try {
            window.localStorage.setItem(
                GARAGE_FILTERS_STORAGE_KEY,
                JSON.stringify({
                    showFilters,
                    viewMode,
                    sortBy,
                }),
            );
            window.dispatchEvent(new Event("garage-filters-changed"));
        } catch {
            // Ignore storage errors in private mode or restricted environments.
        }
    }, [showFilters, viewMode, sortBy]);

    function handleOpenTransferAllModal() {
        if (!selectedPodiumId || offlineState) return;
        setTransferDestinationPodiumId("");
        setTransferNote("");
        setShowTransferAllModal(true);
    }

    function handleCloseTransferAllModal() {
        setShowTransferAllModal(false);

        const params = new URLSearchParams(location.search);
        if (params.get("transferAll") !== "true") return;

        params.delete("transferAll");
        const nextSearch = params.toString();
        history.replace({
            pathname: location.pathname,
            search: nextSearch ? `?${nextSearch}` : "",
        });
    }

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const shouldOpenTransferAll = params.get("transferAll") === "true";

        if (!shouldOpenTransferAll || offlineState) return;

        if (!selectedPodiumId) {
            toast.info("Please select an active podium before using Transfer All.", { autoClose: 1200 });

            params.delete("transferAll");
            const nextSearch = params.toString();
            history.replace({
                pathname: location.pathname,
                search: nextSearch ? `?${nextSearch}` : "",
            });
            return;
        }

        setTransferDestinationPodiumId("");
        setTransferNote("");
        setShowTransferAllModal(true);
    }, [history, location.pathname, location.search, selectedPodiumId, offlineState]);

    async function handleInitiateTransferAll() {
        if (offlineState || !selectedPodiumId || !transferDestinationPodiumId || isTransferAllBusy || isTransferAllPreviewLoading || transferAllPreviewCount < 1) return;

        try {
            setIsTransferAllBusy(true);
            const res = await ValetApi.initiateTransferAll(Number(selectedPodiumId), Number(transferDestinationPodiumId), transferNote || "");
            const result = res?.data?.result || {};
            const initiatedCount = Number(result.initiatedCount || 0);
            const skippedCount = Number(result.skippedCount || 0);

            if (initiatedCount > 0 && skippedCount > 0) {
                toast.info(`Transfer all complete: ${initiatedCount} initiated, ${skippedCount} skipped.`, { autoClose: 1600 });
            } else if (initiatedCount > 0) {
                toast.success(`Transfer all initiated for ${initiatedCount} vehicle${initiatedCount === 1 ? "" : "s"}.`, { autoClose: 1400 });
            } else {
                toast.info("No transferable vehicles found for this podium.", { autoClose: 1400 });
            }

            handleCloseTransferAllModal();
            setTransferDestinationPodiumId("");
            setTransferNote("");
            setRefreshGarageTick((v) => v + 1);
        } catch (err) {
            toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to initiate transfer all", { autoClose: 1200 });
        } finally {
            setIsTransferAllBusy(false);
        }
    }

    return (
        <section className="garage-list-page">
            <div className="garage-list-bg">
                <div className="garage-list-top">{/* optional header content only */}</div>
                {offlineState ? (
                    <Alert
                        color="warning"
                        fade={false}>
                        <strong>OFFLINE MODE - Read only</strong>
                        <div>{offlineState.savedAt ? `Cached active vehicles. Last synced: ${new Date(offlineState.savedAt).toLocaleString()}.` : "No current saved garage is available for this session and podium."}</div>
                        <div>Payments, check-in, check-out, and transfers are unavailable.</div>
                        <Button
                            size="sm"
                            color="secondary"
                            onClick={() => setRefreshGarageTick((previous) => previous + 1)}>
                            Retry Connection
                        </Button>
                    </Alert>
                ) : cacheWarning ? (
                    <Alert
                        color="warning"
                        fade={false}>
                        Live garage loaded. Emergency device cache is unavailable.
                    </Alert>
                ) : null}
                <div className="garage-list-controls-bar">
                    <button
                        type="button"
                        className="garage-list-filters-toggle"
                        onClick={() => setShowFilters((v) => !v)}>
                        {showFilters ? "Hide Filters" : "Show Filters"}
                    </button>
                </div>

                {showFilters && (
                    <div className="garage-list-controls">
                        <div className="garage-list-dropdown">
                            <label htmlFor="garage-view-mode">View</label>
                            <select
                                id="garage-view-mode"
                                className="garage-list-select"
                                disabled={Boolean(offlineState)}
                                value={viewMode}
                                onChange={(e) => setViewMode(e.target.value)}>
                                <option value="operational">Default</option>
                                <option value="checked_out">Checked Out</option>
                                <option value="all">All Status</option>
                            </select>
                        </div>
                        <div className="garage-list-dropdown">
                            <label htmlFor="garage-sort-by">Sort By</label>
                            <select
                                id="garage-sort-by"
                                className="garage-list-select"
                                disabled={Boolean(offlineState)}
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}>
                                <option value="default">Default</option>
                                <option value="mobile">Mobile</option>
                                <option value="ticket">Ticket</option>
                                <option value="make">Make</option>
                                <option value="status">Status</option>
                                <option value="arrival">Arrival</option>
                                <option value="departure">Departure</option>
                            </select>
                        </div>
                    </div>
                )}

                <div className="garage-list-scroll-container">
                    <div className="garage-list-grid">
                        {garageData.length > 0 ? (
                            <GarageCard
                                garageData={garageData}
                                paymentByVehicleId={offlineState ? {} : paymentByVehicleId}
                                paymentsEnabled={isPaymentsFeatureEnabled === true}
                                readOnly={Boolean(offlineState)}
                            />
                        ) : (
                            <div className="garage-list-empty">
                                <p>{offlineState ? "No cached vehicles match this view." : "No Vehicles"}</p>
                            </div>
                        )}
                    </div>
                </div>

                <Modal
                    isOpen={showTransferAllModal}
                    toggle={handleCloseTransferAllModal}
                    modalClassName="transfer-all-modal-theme"
                    centered>
                    <ModalHeader toggle={handleCloseTransferAllModal}>Transfer All Vehicles</ModalHeader>
                    <ModalBody>
                        <div style={{ marginBottom: "8px" }}>
                            Current Podium: <strong>{activePodiumName}</strong>
                        </div>
                        <div style={{ marginBottom: "8px" }}>
                            Vehicles eligible now: <strong>{isTransferAllPreviewLoading ? "Loading..." : transferAllPreviewCount}</strong>
                        </div>
                        <div style={{ marginBottom: "8px" }}>
                            Destination Podium
                            <Input
                                type="select"
                                value={transferDestinationPodiumId}
                                onChange={(e) => setTransferDestinationPodiumId(e.target.value)}>
                                <option value="">Select destination podium</option>
                                {destinationPodiums.map((p) => (
                                    <option
                                        key={p.id}
                                        value={p.id}>
                                        {p.name}
                                    </option>
                                ))}
                            </Input>
                        </div>
                        <div>
                            Note (optional)
                            <Input
                                type="text"
                                value={transferNote}
                                onChange={(e) => setTransferNote(e.target.value)}
                                placeholder="Transfer handoff notes"
                            />
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color="secondary"
                            onClick={handleCloseTransferAllModal}>
                            Close
                        </Button>
                        <Button
                            color="warning"
                            disabled={!transferDestinationPodiumId || isTransferAllBusy || isTransferAllPreviewLoading || transferAllPreviewCount < 1}
                            onClick={handleInitiateTransferAll}>
                            {isTransferAllBusy ? "Transferring..." : "Transfer All"}
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* NEW: bottom dock */}
                <div className="garage-list-bottom-dock">
                    <div className="garage-list-bottom-search">
                        <MobileSearchBar
                            podiumId={selectedPodiumId}
                            locationId={activeLocationId || userDetail?.locationId}
                            initialMobile={mobileQuery}
                            showReset={true}
                        />
                    </div>

                    <Link
                        to={offlineState ? "#" : "/addVehicle"}
                        onClick={(event) => {
                            if (offlineState) event.preventDefault();
                        }}
                        aria-disabled={Boolean(offlineState)}
                        tabIndex={offlineState ? -1 : undefined}
                        className="garage-list-add-btn"
                        aria-label="Add Vehicle">
                        +
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default GarageList;
